import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ExternalLink, Loader2, Lock, RotateCcw, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import {
  useAcceptMappings, useMappings, useSetMapping,
  type MapProduct, type MapRecord, type MapValue, type ProductMeta,
} from '@/api/mappings';
import { useClientScope } from '@/contexts/ClientScopeContext';
import { companyPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Admin → Client mappings: every record in every product against its Autotask company (Autotask is
 * the source of truth). Three views of one dataset (server/mappings.ts):
 *  - Needs attention — unmapped records across all products, with suggestions + bulk accept.
 *  - By client — one Autotask company: what's linked in each product, and link more.
 *  - All records — everything, filterable by product and status.
 * Datto RMM and IT Glue links belong to their own Autotask integrations: shown read-only with a
 * "Fix in …" link (by decision, so the systems never disagree).
 */

type View = 'attention' | 'client' | 'all';

const SOURCE_CHIP: Record<MapRecord['source'], { label: string; cls: string }> = {
  vendor:  { label: 'Vendor link', cls: 'bg-muted text-foreground' },
  auto:    { label: 'Automatic',   cls: 'bg-primary/10 text-primary' },
  manual:  { label: 'Mapped',      cls: 'bg-success-soft text-success' },
  ignored: { label: 'Ignored',     cls: 'bg-muted text-muted-foreground' },
  none:    { label: 'Not mapped',  cls: 'bg-warning-soft text-warning' },
};

export default function AdminMappingsPage() {
  const { data, isLoading, error, isFetching } = useMappings();
  const [view, setView] = useState<View>('attention');
  const [product, setProduct] = useState<MapProduct | 'all'>('all');
  const records = data?.records ?? [];
  const products = data?.products ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">Client mappings {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}</h1>
          <p className="text-xs text-muted-foreground">
            Every product's records against their Autotask company: Autotask is the source of truth. Datto RMM and IT Glue links come from their own
            Autotask integrations and are fixed there.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Gathering records from every product…</div>
      ) : error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
      ) : (
        <>
          <ProductTiles products={products} records={records} active={product} onPick={(p) => { setProduct(p === product ? 'all' : p); if (view === 'client') setView('attention'); }} />

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-lg bg-muted p-1">
              {([['attention', 'Needs attention'], ['client', 'By client'], ['all', 'All records']] as const).map(([id, label]) => (
                <button key={id} type="button" onClick={() => setView(id)}
                  className={cn('rounded-md px-3 py-1 text-xs font-medium', view === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
                  {label}
                </button>
              ))}
            </div>
            {product !== 'all' && view !== 'client' && (
              <button type="button" onClick={() => setProduct('all')} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {products.find((p) => p.id === product)?.label} <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {view === 'attention' && <AttentionView records={records} products={products} product={product} />}
          {view === 'client' && <ClientView records={records} products={products} />}
          {view === 'all' && <AllView records={records} products={products} product={product} />}
        </>
      )}
    </div>
  );
}

// ---------------- Tiles ----------------

function ProductTiles({ products, records, active, onPick }: { products: ProductMeta[]; records: MapRecord[]; active: MapProduct | 'all'; onPick: (p: MapProduct) => void }) {
  return (
    // Nine products: 3 × 3 divides evenly (the settings column is too narrow for nine across), and on a
    // phone the odd ninth tile spans both columns rather than sitting alone.
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {products.map((p) => {
        const mine = records.filter((r) => r.product === p.id);
        const linked = mine.filter((r) => r.companyId != null).length;
        const open = mine.filter((r) => r.source === 'none').length;
        return (
          <button key={p.id} type="button" onClick={() => onPick(p.id)} title={p.error ?? p.label}
            className={cn('min-w-0 rounded-lg border bg-card px-3 py-2 text-left shadow-sm transition-colors hover:border-ring last:col-span-2 sm:last:col-span-1', active === p.id ? 'border-ring ring-1 ring-ring/40' : 'border-border')}>
            <div className="flex min-w-0 items-center gap-1 text-xs font-medium text-muted-foreground">
              {p.owner === 'vendor' && <Lock className="h-3 w-3 shrink-0" />}<span className="truncate">{p.label}</span>
            </div>
            {p.error ? (
              <div className="mt-1 text-xs text-destructive">Unavailable</div>
            ) : (
              <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
                <span className="whitespace-nowrap text-lg font-semibold tabular-nums text-foreground">{linked}<span className="text-sm font-normal text-muted-foreground"> / {mine.length}</span></span>
                <span className={cn('whitespace-nowrap text-[11px]', open ? 'text-warning' : 'text-muted-foreground')}>{open ? `${open} not mapped` : 'all mapped'}</span>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------- Needs attention ----------------

function AttentionView({ records, products, product }: { records: MapRecord[]; products: ProductMeta[]; product: MapProduct | 'all' }) {
  const accept = useAcceptMappings();
  const open = records.filter((r) => r.source === 'none' && (product === 'all' || r.product === product));
  const strong = open.filter((r) => r.editable && r.suggestion?.strong);

  async function acceptStrong() {
    try {
      const res = await accept.mutateAsync(strong.map((r) => ({ product: r.product, id: r.id, companyId: r.suggestion!.companyId, companyName: r.suggestion!.companyName })));
      toast.success(`Mapped ${res.saved} record${res.saved === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error('Failed to accept suggestions', { description: err instanceof Error ? err.message : '' });
    }
  }

  if (!open.length) return <div className="rounded-lg border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">Everything is mapped. 🎉</div>;

  const groups = products.map((p) => ({ p, rows: open.filter((r) => r.product === p.id) })).filter((g) => g.rows.length);
  return (
    <div className="space-y-4">
      {strong.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm">
          <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />{strong.length} record{strong.length === 1 ? ' has' : 's have'} a strong suggestion.</span>
          <Button size="sm" onClick={acceptStrong} disabled={accept.isPending}>
            {accept.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Accept {strong.length} strong match{strong.length === 1 ? '' : 'es'}
          </Button>
        </div>
      )}
      {groups.map(({ p, rows }) => (
        <section key={p.id} className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-divider bg-muted/40 px-4 py-2 text-xs">
            <span className="font-semibold text-foreground">{p.label}</span>
            <span className="text-muted-foreground">{rows.length} {p.unit}{rows.length === 1 ? '' : 's'} not mapped{p.owner === 'vendor' && ' · fix the link in ' + p.label}</span>
          </div>
          <RecordTable rows={rows} />
        </section>
      ))}
    </div>
  );
}

// ---------------- By client ----------------

function ClientView({ records, products }: { records: MapRecord[]; products: ProductMeta[] }) {
  const { scope } = useClientScope();
  const [company, setCompany] = useState<CompanyOption | null>(scope ? { id: scope.companyId, name: scope.name } : null);
  const set = useSetMapping();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-80"><CompanySearch value={company} onChange={setCompany} placeholder="Pick an Autotask company…" /></div>
        {company && <Link to={companyPath(company.id)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">Orbit →</Link>}
      </div>
      {!company ? (
        <p className="text-sm text-muted-foreground">Pick a company to see and change its links in every product. The header's client picker pre-fills this.</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {products.map((p) => {
            const linked = records.filter((r) => r.product === p.id && r.companyId === company.id);
            const suggested = records.filter((r) => r.product === p.id && r.source === 'none' && r.suggestion?.companyId === company.id);
            const unmapped = records.filter((r) => r.product === p.id && r.source === 'none' && r.editable);
            return (
              <section key={p.id} className="rounded-lg border border-border bg-card shadow-sm">
                <div className="flex items-center justify-between border-b border-divider px-4 py-2 text-xs">
                  <span className="flex items-center gap-1 font-semibold text-foreground">{p.owner === 'vendor' && <Lock className="h-3 w-3 text-muted-foreground" />}{p.label}</span>
                  <span className={linked.length ? 'text-muted-foreground' : 'text-warning'}>{linked.length ? `${linked.length} linked` : 'nothing linked'}</span>
                </div>
                <div className="px-2 py-1">
                  {linked.map((r) => <ClientRecord key={r.id} r={r} />)}
                  {suggested.map((r) => <ClientRecord key={`s${r.id}`} r={r} />)}
                  {!linked.length && !suggested.length && <p className="px-2 py-1.5 text-xs text-muted-foreground">No {p.unit} linked to {company.name}.</p>}
                </div>
                {p.owner === 'cosmos' && unmapped.length > 0 && (
                  <div className="border-t border-divider px-4 py-2">
                    <select value="" disabled={set.isPending}
                      onChange={(e) => {
                        const r = unmapped.find((x) => x.id === e.target.value);
                        if (r) set.mutate({ product: r.product, id: r.id, value: { companyId: company.id, companyName: company.name } }, {
                          onSuccess: () => toast.success(`${r.name} → ${company.name}`),
                          onError: (err) => toast.error('Failed to map', { description: err instanceof Error ? err.message : '' }),
                        });
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground">
                      <option value="">Link an unmapped {p.unit} to {company.name}…</option>
                      {unmapped.map((r) => <option key={r.id} value={r.id}>{r.name}{r.detail ? ` (${r.detail})` : ''}</option>)}
                    </select>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ClientRecord({ r }: { r: MapRecord }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/30">
      <div className="min-w-0">
        <div className="truncate text-foreground" title={r.name}>{r.name}</div>
        <div className="truncate text-[11px] text-muted-foreground">{[r.detail, r.sourceNote].filter(Boolean).join(' · ')}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2"><SourceChip r={r} /><Actions r={r} /></div>
    </div>
  );
}

// ---------------- All records ----------------

function AllView({ records, products, product }: { records: MapRecord[]; products: ProductMeta[]; product: MapProduct | 'all' }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'any' | MapRecord['source']>('any');
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return records
      .filter((r) => (product === 'all' || r.product === product) && (status === 'any' || r.source === status))
      .filter((r) => !s || [r.name, r.detail, r.companyName].some((v) => v?.toLowerCase().includes(s)));
  }, [records, product, status, q]);
  const label = (p: MapProduct) => products.find((x) => x.id === p)?.label ?? p;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput withIcon placeholder="Record or company" className="w-72" value={q} onValueChange={setQ} />
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
          <option value="any">Any status</option>
          {(Object.keys(SOURCE_CHIP) as MapRecord['source'][]).map((s) => <option key={s} value={s}>{SOURCE_CHIP[s].label}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">{rows.length} records</span>
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <RecordTable rows={rows.slice(0, 400)} productLabel={product === 'all' ? label : undefined} />
        {rows.length > 400 && <div className="border-t border-divider px-3 py-2 text-xs text-muted-foreground">Showing 400 of {rows.length}: narrow with the search or a filter.</div>}
      </div>
    </div>
  );
}

// ---------------- Shared rows / actions ----------------

function RecordTable({ rows, productLabel }: { rows: MapRecord[]; productLabel?: (p: MapProduct) => string }) {
  return (
    <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
      <colgroup>{productLabel && <col style={{ width: 120 }} />}<col /><col style={{ width: '30%' }} /><col style={{ width: 104 }} /><col style={{ width: 240 }} /></colgroup>
      <thead className="bg-muted/30 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
        <tr>
          {productLabel && <th className="px-3 py-1.5 font-medium">Product</th>}
          <th className="px-3 py-1.5 font-medium">Record</th>
          <th className="px-3 py-1.5 font-medium">Autotask company</th>
          <th className="px-3 py-1.5 font-medium">Status</th>
          <th className="px-3 py-1.5 font-medium" />
        </tr>
      </thead>
      <tbody className="divide-y divide-divider">
        {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Nothing matches.</td></tr>}
        {rows.map((r) => (
          <tr key={`${r.product}:${r.id}`} className="align-top hover:bg-muted/20">
            {productLabel && <td className="truncate px-3 py-2 text-xs text-muted-foreground">{productLabel(r.product)}</td>}
            <td className="overflow-hidden px-3 py-2">
              <div className="truncate font-medium text-foreground" title={r.name}>{r.name}</div>
              {r.detail && <div className="truncate text-[11px] text-muted-foreground" title={r.detail}>{r.detail}</div>}
            </td>
            <td className="overflow-hidden px-3 py-2 text-xs">
              {r.companyId != null
                ? <Link to={companyPath(r.companyId)} className="block truncate text-foreground hover:underline" title={r.companyName ?? ''}>{r.companyName}</Link>
                : r.suggestion
                  ? <span className="block truncate text-muted-foreground" title={r.suggestion.reason}>Suggested: <span className="text-foreground">{r.suggestion.companyName}</span>{r.suggestion.strong ? ' (strong)' : ''}</span>
                  : <span className="text-muted-foreground">—</span>}
              {r.sourceNote && <div className="truncate text-[11px] text-muted-foreground">{r.sourceNote}</div>}
            </td>
            <td className="px-3 py-2"><SourceChip r={r} /></td>
            <td className="px-3 py-2"><Actions r={r} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SourceChip({ r }: { r: MapRecord }) {
  const c = SOURCE_CHIP[r.source];
  return <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold', c.cls)}>{c.label}</span>;
}

/** What you can do with one record: accept the suggestion, map to any company, ignore, reset/unlink — or fix it in the vendor. */
function Actions({ r }: { r: MapRecord }) {
  const set = useSetMapping();
  const [picking, setPicking] = useState(false);
  const save = (value: MapValue, done: string) => set.mutate({ product: r.product, id: r.id, value }, {
    onSuccess: () => { toast.success(done); setPicking(false); },
    onError: (err) => toast.error('Failed to save mapping', { description: err instanceof Error ? err.message : '' }),
  });

  if (!r.editable) {
    return r.fixUrl
      ? <a href={r.fixUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline">Fix in {r.product === 'rmm' ? 'Datto RMM' : 'IT Glue'} <ExternalLink className="h-3 w-3" /></a>
      : <span className="text-xs text-muted-foreground">Managed in the vendor</span>;
  }
  if (picking) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex-1"><CompanySearch value={null} onChange={(c) => c && save({ companyId: c.id, companyName: c.name }, `${r.name} → ${c.name}`)} placeholder="Search Autotask company…" /></div>
        <button type="button" onClick={() => setPicking(false)} className="text-muted-foreground hover:text-foreground" aria-label="Cancel"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  const btn = 'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs hover:bg-muted disabled:opacity-50';
  const autoProduct = r.product === 'backup' || r.product === 'domains' || r.product === 'csp';
  return (
    <div className="flex flex-wrap items-center gap-1">
      {set.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      {r.source === 'none' && r.suggestion && (
        <button type="button" disabled={set.isPending} title={r.suggestion.reason} className={cn(btn, 'font-medium text-primary')}
          onClick={() => save({ companyId: r.suggestion!.companyId, companyName: r.suggestion!.companyName }, `${r.name} → ${r.suggestion!.companyName}`)}>
          <Check className="h-3.5 w-3.5" />Accept
        </button>
      )}
      <button type="button" disabled={set.isPending} className={cn(btn, 'text-foreground')} onClick={() => setPicking(true)}>{r.companyId != null ? 'Change…' : 'Map to…'}</button>
      {r.source !== 'ignored' && (
        <button type="button" disabled={set.isPending} className={cn(btn, 'text-muted-foreground')} title="Not a client (staff home network, test tenant…)" onClick={() => save({ ignored: true }, `${r.name} ignored`)}>Ignore</button>
      )}
      {r.resettable && (
        <button type="button" disabled={set.isPending} className={cn(btn, 'text-muted-foreground')} onClick={() => save(null, autoProduct ? `${r.name} reset to automatic` : `${r.name} unlinked`)}>
          <RotateCcw className="h-3 w-3" />{autoProduct ? 'Reset' : 'Unlink'}
        </button>
      )}
    </div>
  );
}
